import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, MessageSquare, BarChart3, Search, Mail, Receipt, Mic, ClipboardList, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import IntegrationTicker from "@/components/IntegrationTicker";
import Footer from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const features = [
  { title: "Client Research", desc: "Consolidate research from multiple sources into actionable insights for each client.", icon: Search },
  { title: "Meeting Notes", desc: "Auto-capture meeting notes and action items with AI-powered transcription.", icon: MessageSquare },
  { title: "Portfolio Reports", desc: "Generate performance reports tailored to each client's preferences.", icon: BarChart3 },
  { title: "Document Processing", desc: "Extract data from statements and regulatory filings automatically.", icon: FileText },
  { title: "Email Drafts", desc: "Create personalized client communications with context-aware suggestions.", icon: Mail },
  { title: "Invoice Reconciliation", desc: "Generates one-click invoices with automatic fee capture, approvals, and clean audit trails.", icon: Receipt },
  { title: "Voice Memos", desc: "Transcribe voice notes into structured data and CRM updates.", icon: Mic },
  { title: "Task Automation", desc: "Automate repetitive workflows with intelligent task routing.", icon: ClipboardList },
  { title: "Call Summaries", desc: "Generate concise summaries from client calls with key action points.", icon: Phone },
];

const faqItems = [
  { q: "What is Layla?", a: "Layla is Compu.J's modular agentic AI platform designed for wealth management firms. It connects your existing systems, automates workflows, and delivers actionable intelligence across your entire operation." },
  { q: "How does Layla help my advisory practice?", a: "Layla automates time-consuming tasks like research aggregation, client reporting, meeting notes, and compliance documentation - freeing advisors to spend more time with clients." },
  { q: "Can Layla integrate with my CRM or other software?", a: "Yes. Layla integrates seamlessly with major platforms including Salesforce, Bloomberg, PitchBook, Gmail, SharePoint, and many more through our flexible integration layer." },
  { q: "Can Layla build custom workflows?", a: "Absolutely. Layla is configured around your exact operating model - workflows, roles, approvals, and controls - so the platform fits how your firm actually works." },
  { q: "How secure is the data on Layla?", a: "Layla is engineered to enterprise-grade security standards with 256-bit encryption, ISO 27001 certification, 24/7 monitoring, and full GDPR compliance." },
];

const testimonials = [
  {
    quote: "By combining our expertise in CLM with Compu J's expertise, we can help wealth managers achieve new levels of efficiency, client engagement, and business growth.",
    name: "Gary Lintres",
    title: "CEO & Co-Founder"
  },
  {
    quote: "Compu.J transformed how we handle client onboarding. What used to take days now happens in hours, with better compliance and happier clients.",
    name: "Ray Eden",
    title: "CEO & Founder, CCK"
  },
  {
    quote: "The AI-powered insights has given us a genuine edge. We're making better decisions faster, backed by data we can trust.",
    name: "David Wood",
    title: "CEO & Co-Founder, LightingZone"
  },
];

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".section-reveal");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function TestimonialsCarousel() {
  const [currentIdx, setCurrentIdx] = useState(0);
  const prevTestimonial = () => setCurrentIdx((p) => (p === 0 ? testimonials.length - 1 : p - 1));
  const nextTestimonial = () => setCurrentIdx((p) => (p === testimonials.length - 1 ? 0 : p + 1));

  return (
    <div className="section-reveal max-w-5xl mx-auto text-center">
      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#AF4AFA', fontFamily: "'Lora', serif" }}>Testimonials</p>
      <h2 className="text-3xl md:text-5xl font-medium mb-4" style={{ fontFamily: "'Lora', serif", color: '#F5F5F5' }}>
        Trusted by our clients
      </h2>
      <p
  className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-12"
  style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
>
  We deliver end-to-end technology and AI transformation that drives real operational efficiency.
</p>

      <div className="relative">
        <div
          className="rounded-2xl p-8 md:p-12 max-w-3xl mx-auto"
          style={{
  background: "linear-gradient(180deg, #11121B 0%, #31344E 55%)",
}}>
          <p className="text-base md:text-lg italic leading-relaxed mb-6" style={{ color: '#F5F5F5', fontFamily: "'Lora', serif" }}>
            "{testimonials[currentIdx].quote}"
          </p>
          <p className="text-sm font-medium" style={{ color: '#F5F5F5', fontFamily: "'Lora', serif" }}>{testimonials[currentIdx].title}</p>
          <p className="text-xs" style={{ color: '#A1A1AA', fontFamily: "'Lora', serif" }}>{testimonials[currentIdx].name}</p>
        </div>

        <button
          onClick={prevTestimonial}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors"
          style={{
  backgroundColor: "rgba(76, 81, 121, 0.25)",
  border: "1px solid rgba(76, 81, 121, 0.75)",
}}
        >
          <ChevronLeft size={20} style={{ color: '#A1A1AA' }} />
        </button>
        <button
          onClick={nextTestimonial}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors"
          style={{ border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <ChevronRight size={20} style={{ color: '#A1A1AA' }} />
        </button>

        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIdx(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === currentIdx ? 'bg-[#6373FA] w-4' : 'bg-white/20'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const pageRef = useSectionReveal();

  return (
    <div ref={pageRef} style={{ backgroundColor: '#050508' }}>
      {/* ===== HERO SECTION - Pixel Perfect ===== */}
      <section data-testid="hero-section" className="relative min-h-screen flex flex-col items-center pt-28 md:pt-32 overflow-hidden">
        {/* Subtle purple glow behind mockup area */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[800px] h-[600px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(99, 115, 250, 0.15) 0%, transparent 60%)',
            filter: 'blur(60px)'
          }}
        />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-10 md:mt-14">
          <h1
            className="animate-hero-title text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-6"
            style={{ fontFamily: "'Lora', serif", color: '#F5F5F5' }}
            data-testid="hero-headline"
          >
            The Future of<br />Wealth Management
          </h1>
          <p
  className="animate-hero-sub text-lg md:text-xl lg:text-2xl mb-8 max-w-2xl mx-auto leading-relaxed"
  style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
  data-testid="hero-subheadline"
>
  Introducing Layla — Portfolio intelligence, powered by AI and built for control.
</p>
          <div className="animate-hero-btn">
            <Link
              to="/contact"
              data-testid="hero-cta-btn"
              className="inline-block text-sm font-medium px-6 py-2.5 rounded-full transition-colors"
              style={{
                border: '1px solid rgba(255,255,255,0.3)',
                color: '#F5F5F5',
                fontFamily: "'Lora', serif"
              }}
            >
              Book Demo
            </Link>
          </div>
        </div>

      
{/* Hero Mockup Frame with purple glow */}
<div className="animate-hero-mockup relative z-10 mt-12 max-w-4xl mx-auto px-6 w-full">
  <div className="hero-mockup-shell" data-testid="hero-mockup">
    {/* App Header */}
    <div className="mock-topbar">
      <div className="mock-brand">
        <span className="mock-brand-dot" />
        <span className="mock-brand-text">Compu.J</span>
      </div>
      

      <div className="mock-nav">
        <span className="mock-nav-active">Market Research</span>
        <span className="mock-nav-item">Mandates</span>
        <span className="mock-nav-item">Invoices</span>
        <span className="mock-nav-item">Home</span>
      </div>
    </div>

    {/* App Content */}
    <div className="mock-body">
      <div className="mock-head-row">
        <div>
          <div className="mock-title">Daily Digest</div>
          <div className="mock-subtitle">Consolidated broker research • Thu 05 Dec</div>
        </div>

        <button className="mock-filter" type="button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6h16M7 12h10M10 18h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Filter
        </button>
      </div>

      <div className="mock-stats">
        <div className="mock-stat">
          <div className="mock-stat-label">Sources Ingested</div>
          <div className="mock-stat-value">10</div>
        </div>
        <div className="mock-stat">
          <div className="mock-stat-label">Clients Affected</div>
          <div className="mock-stat-value">12</div>
        </div>
      </div>

      <div className="mock-section">
        <div className="mock-section-head">
          <div className="mock-section-title">Macro & Rates</div>
          <div className="mock-section-line" />
        </div>

        <div className="mock-feed">
          <div className="mock-card">
            <div className="mock-card-left">
              <div className="mock-checkbox" />
            </div>

            <div className="mock-card-main">
              <div className="mock-card-top">
                <div className="mock-bank gs">
                  Goldman<br />Sachs
                </div>
                <div className="mock-risk high">High</div>
              </div>

              <div className="mock-card-title">
                Overnight markets firmed as wage data cooled, and the curve steepened ahead of a data-heavy week.
              </div>
              <div className="mock-card-text">
                Base case: Softer wage momentum reduces immediate re-pricing risk, supporting a modest risk-on tone into major macro releases.
                What to watch: The next inflation print and Fed communication will determine whether the front-end rally holds or reverses.
              </div>
            </div>
          </div>

          <div className="mock-card">
            <div className="mock-card-left">
              <div className="mock-checkbox" />
            </div>

            <div className="mock-card-main">
              <div className="mock-card-top">
                <div className="mock-bank jpm">JPM</div>
                <div className="mock-risk high">High</div>
              </div>

              <div className="mock-card-title">
                The ECB is likely to keep a restrictive tone even as growth expectations soften.
              </div>
              <div className="mock-card-text">
                Base case: The ECB prioritises services inflation persistence, so guidance stays tight even if activity data disappoints.
                What to watch: Any shift in language or projections that validates faster disinflation could pull forward cuts and rally the front end.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

</section>

        {/* ===== LET LAYLA AUTOMATE ===== */}
<section
  data-testid="features-section"
  className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden"
>
  {/* Stronger glow */}
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      background:
        "radial-gradient(900px 520px at 50% 85%, rgba(99,115,250,0.26) 0%, rgba(175,74,250,0.14) 35%, transparent 70%)",
      filter: "blur(10px)",
    }}
  />

  <div className="section-reveal max-w-7xl mx-auto relative z-10">
    <h2
      className="text-3xl md:text-5xl font-medium text-center mb-4"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
    >
      Let Layla Automate
      <br />
      your Workflows
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
      {features.map((f, i) => (
        <div
          key={f.title}
          className="feature-tile rounded-lg p-6"
          style={{
            background: "rgba(255, 255, 255, 0.03)",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
          data-testid={`feature-card-${i}`}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
              style={{
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.10)",
              }}
            >
              <f.icon size={20} style={{ color: "#7A85E2" }} />
            </div>

            <div className="min-w-0">
              {/* Same title height for every tile */}
              <h3
  className="text-base md:text-lg font-medium mb-2 leading-snug min-h-[56px] line-clamp-2"
  style={{ color: "#F5F5F5", fontFamily: "'Lora', serif" }}
>
  {f.title}
</h3>

              <p
                className="text-sm md:text-base leading-relaxed"
                style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
              >
                {f.desc}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* CTA button */}
    <div className="text-center mt-10">
      <Link
        to="/contact"
        className="cta-gradient inline-flex items-center justify-center gap-2 text-sm font-medium px-6 py-2.5 rounded-full"
        style={{ fontFamily: "'Lora', serif" }}
      >
        See How
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  </div>
</section>

        {/* ===== INTEGRATIONS ===== */}
        <section data-testid="integrations-section" className="py-16 px-6 md:px-12">
          <div className="section-reveal text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-3xl md:text-5xl font-medium mb-4" style={{ fontFamily: "'Lora', serif", color: '#F5F5F5' }}>
              Integrates seamlessly to your systems
            </h2>
            <p
  className="text-base md:text-lg leading-relaxed"
  style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
>
  Whether it's connecting to your existing CRM, data providers, or compliance tools, Layla plugs in and starts working.
</p>
          </div>
          <IntegrationTicker />
        </section>

        {/* ===== TESTIMONIALS ===== */}
        <section data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12">
          <TestimonialsCarousel />
        </section>

        {/* ===== AI-NATIVE SECTION ===== */}
<section
  data-testid="ai-native-section"
  className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden ai-native-bg"
>
  {/* Optional wave / particles overlays */}
  <img
  src="/assets/wave-form-new.svg"
  alt=""
  aria-hidden="true"
  className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-75"
/>

  <div className="section-reveal max-w-6xl mx-auto relative z-10">
    <h2
      className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
    >
      Make your wealth
      <br />
      business{" "}
      <span className="ai-gradient-text">AI-native</span>
      <br />
      without rebuilding
      <br />
      everything.
    </h2>

    <div className="mt-10 md:mt-12 max-w-3xl space-y-6">
      <p
        className="text-base md:text-lg lg:text-xl leading-relaxed"
        style={{ color: "rgba(245,245,245,0.92)", fontFamily: "'Lora', serif" }}
      >
        Compu.J deploys Layla™, a modular agentic AI platform designed to connect across
        systems, unify data, and orchestrate high-stakes workflows end-to-end.
      </p>

      <p
        className="text-base md:text-lg lg:text-xl leading-relaxed"
        style={{ color: "rgba(245,245,245,0.92)", fontFamily: "'Lora', serif" }}
      >
        From configuration to adoption, we partner with your teams to replace fragmented
        tooling with secure, scalable capabilities that drive real operational and
        client-impact outcomes.
      </p>
    </div>
  </div>
</section>

        {/* ===== WHY US ===== */}
        <section
          data-testid="why-us-section"
          className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24"
        >
          {/* subtle background vignette like your screenshot */}
          <div
  className="absolute inset-0 pointer-events-none why-glow"
  style={{
    background:
      "radial-gradient(1100px 650px at 50% 75%, rgba(99,115,250,0.28) 0%, rgba(175,74,250,0.18) 40%, transparent 72%), radial-gradient(700px 420px at 15% 20%, rgba(255,255,255,0.04) 0%, transparent 60%)",
    filter: "blur(12px)",
  }}
/>

          <div className="section-reveal max-w-6xl mx-auto relative z-10">
            <h2
              className="text-3xl md:text-4xl font-medium mb-10"
              style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
            >
              Why Us?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {[
                {
                  title: "Hyper-personalisation",
                  desc:
                    "Configured around your exact operating model — workflows, roles, approvals, and controls — so the platform fits how your firm actually works.",
                },
                {
                  title: "End-to-end workflows",
                  desc:
                    "One connected platform across the full client and investment lifecycle — from onboarding and servicing to oversight, reporting, and execution.",
                },
                {
                  title: "Flexible Deployment",
                  desc:
                    "We deploy where it works best — your private cloud, an enterprise provider, or the Compu.J cloud — so you stay in full control of your data, your systems, and your outputs.",
                },
                {
                  title: "AI Insight Engine",
                  desc:
                    "Turns your proprietary data into real-time insights and next-best actions — delivered directly into the tools and workflows your teams use daily.",
                },
                {
                  title: "Multi-Modal Intelligence",
                  desc:
                    "Access intelligence through chat, voice, or video — so teams can move faster without compromising compliance or clarity.",
                },
                {
                  title: "Custom Applications",
                  desc:
                    "Build white-labelled apps, dashboards, and portals — tailored to your users, your brand, and the way your teams operate.",
                },
              ].map((item, i) => (
                <div key={i} className="why-card" data-testid={`why-card-${i}`}>
                  <h3
                    className="text-xl md:text-2xl font-medium mb-3"
                    style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ fontFamily: "'Lora', serif", color: "#A1A1AA" }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section data-testid="faq-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
          <div className="section-reveal max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-medium mb-8 text-center" style={{ fontFamily: "'Lora', serif", color: '#F5F5F5' }}>
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-lg px-6"
                  style={{
  background: "rgba(255, 255, 255, 0.03)",
  border: "1px solid rgba(255, 255, 255, 0.10)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
}}
                >
                  <AccordionTrigger className="text-base md:text-lg text-left py-5" style={{ color: "#F5F5F5", fontFamily: "'Lora', serif" }}>
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base md:text-lg pb-5 leading-relaxed" style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <Footer />
    </div>
  );
}
